import React, { useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';
import { get, set } from 'lodash';
import { bindActionCreators } from 'redux';
import uuid from 'uuid/v4';
import { actions as reduxCustomerActions } from '../../customer/redux/customerActions';
import { actions as reduxProductActions, dateToApi } from '../redux/productActions';

import { actions } from '../../broker/redux/brokerActions';
import {
  authUserId,
  getOrganisationId,
  isBroker,
  isBrokerOrganisation,
} from '../../auth/redux/authActions';
import { actions as postcodeActions } from '../../../common/components/Postcode/redux/PostcodeActions';

import getIncludedResource from '../../../helpers/getIncludedResource';
import PersonalAccidentForm from '../components/PersonalAccidentForm/PersonalAccidentForm';
import { openMenu } from '../../layout/redux/sideNavAction';

const PersonalAccidentFormContainer = ({
  source,
  dispatch,
  productId,
  policyId,
  isMta,
  application,
  currentProduct,
  product,
  productActions,
  isCloned,
  user,
  customer,
  brokerId,
  channelId,
  isBrokerChannel,
}) => {
  const [sortedPremiums, setSortedPremiums] = useState({
    standard: [],
    enhanced: [],
  });
  const [issuingError, setIssuingError] = useState('');

  useEffect(
    () => {
      dispatch(openMenu(false));
    },
    [dispatch],
  );

  const getNewInitialValues = useMemo(
    () => (
      sourceId,
      newCustomer,
      newProductId,
      newUser,
      directDebitQuestions,
      brokerId,
      channelId,
      isBrokerChannel,
    ) => {
      const ddQuestions = directDebitQuestions.map((q) => ({
        question: q,
        answer: '',
      }));
      const currentCustomerAttributes = get(newCustomer, 'data.attributes');
      const adults = [
        {
          title: get(currentCustomerAttributes, 'title', ''),
          first_name: get(currentCustomerAttributes, 'first_name', ''),
          last_name: get(currentCustomerAttributes, 'last_name', ''),
          dob: get(currentCustomerAttributes, 'dob', ''),
          email: get(currentCustomerAttributes, 'email', ''),
          phone1: get(currentCustomerAttributes, 'phone1', ''),
          sanction_id: get(currentCustomerAttributes, 'sanction_id', uuid()),
          sanctions_check: get(currentCustomerAttributes, 'sanctions_check', []),
          marketing_options: {
            email: -1,
            sms: -1,
            phone: -1,
            post: -1,
          },
          address: {
            line1: get(currentCustomerAttributes, 'address.line1', ''),
            line2: get(currentCustomerAttributes, 'address.line2', ''),
            line3: get(currentCustomerAttributes, 'address.line3', ''),
            town: get(currentCustomerAttributes, 'address.town', ''),
            county: get(currentCustomerAttributes, 'address.county', ''),
            postcode: get(currentCustomerAttributes, 'address.postcode', ''),
          },
        },
      ];

      return {
        data: {
          type: 'application',
          attributes: {
            quote_reference: '',
            quote_type: '',
            metadata: {
              communications: {
                email: !isBroker() && !isBrokerOrganisation(),
              },
              send_email: true,
              generate_documents: false,
              scheme_type: '',
              group_type: '',
              residency: undefined,
              adults,
              children: [],
              source: '',
              renewals_enabled: true,
              storage: {
                quote_originator: get(newUser, 'data.attributes'),
                quote_originator_organisation_id: getOrganisationId(),
              },
              overrides: null,
              direct_debit_payment_declaration_answers: ddQuestions,
              campaign: '',
              sales_channel: isBrokerChannel ? 'jtc-b2b' : '',
            },
          },
          relationships: {
            broker: {
              data: {
                id: brokerId,
              },
            },
            channel: {
              data: {
                id: channelId,
              },
            },
            source: {
              data: {
                id: sourceId,
              },
            },
            schemes: {
              data: [],
            },
            user: {
              data: {
                id: authUserId(),
              },
            },
            customer: {
              data: {
                id: newCustomer ? newCustomer.data.id : '',
              },
            },
            product: {
              data: {
                id: newProductId,
              },
            },
          },
        },
        meta: {
          payment_type: 'direct debit',
        },
      };
    },
    [],
  );

  const getApplicationInitialValues = useMemo(
    () => (
      existingApplication,
      existingUser,
      existingIsCloned,
      existingIsMta,
      existingProductId,
      directDebitQuestions,
    ) => {
      if (!existingApplication) {
        return null;
      }
      const overrides = get(existingApplication, 'data.attributes.metadata.overrides');
      if (Array.isArray(overrides) && overrides.length === 0) {
        set(existingApplication, 'data.attributes.metadata.overrides', null);
      }
      // Reset any policy details if cloning
      if (existingApplication && existingIsCloned) {
        const ddQuestions = directDebitQuestions.map((q) => ({
          question: q,
          answer: '',
        }));

        set(existingApplication, 'data.id', null);
        set(existingApplication, 'data.attributes.policy_number', '');
        set(existingApplication, 'data.attributes.quote_reference', '');
        set(
          existingApplication,
          'data.attributes.metadata.direct_debit_payment_declaration_answers',
          ddQuestions,
        );
        set(existingApplication, 'data.relationships.schemes.data', []);
        set(
          existingApplication,
          'data.attributes.metadata.storage.quote_originator',
          get(existingUser, 'data.attributes'),
        );
        set(existingApplication, 'data.relationships.product.data.id', existingProductId);
      }

      if (
        get(existingApplication, 'data.attributes.metadata.send_sms') &&
        !get(existingApplication, 'data.attributes.metadata.adults[0].phone2', false)
      ) {
        set(existingApplication, 'data.attributes.metadata.send_sms', false);
      }

      if (
        !existingIsMta &&
        get(existingApplication, 'data.relationships.schemes.data', []).length > 1
      ) {
        const channel = getIncludedResource(
          existingApplication.data,
          existingApplication.included,
          'channel',
        );
        if (get(channel, 'data.attributes.channel_type') === 'B2C') {
          set(existingApplication, 'data.relationships.schemes.data', []);
        }
      }

      if (
        existingApplication &&
        get(existingApplication, 'data.attributes.metadata.storage.quote_originator', [])
          .length < 1 &&
        (get(existingApplication, 'data.attributes.status') === 'renewal invited' ||
          get(existingApplication, 'data.attributes.status') === 'renewal')
      ) {
        // update quote originator on renewals to whomever first edits the quote
        set(
          existingApplication,
          'data.attributes.metadata.storage.quote_originator',
          get(existingUser, 'data.attributes'),
        );
      }

      set(existingApplication, 'meta.payment_type', 'direct debit');
      return existingApplication;
    },
    [],
  );

  const ORDER = ['Silver', 'Gold', 'Platinum'];

  function sortByCoverLevel(premA, premB) {
    if (!premA.scheme || !premB.scheme) {
      return -1;
    }
    const indexOfA = ORDER.indexOf(premA.scheme.cover_level);
    const indexOfB = ORDER.indexOf(premB.scheme.cover_level);

    return indexOfA - indexOfB;
  }

  const checkSanctions = (values) => {
    const schemeId = values?.data?.relationships?.schemes?.data?.[0]?.id;
    const adults = values.data.attributes.metadata.adults.map((adult) => ({
      ...adult,
      sanction_id: uuid(),
    }));
    const insuredChildren = values.data.attributes.metadata.children;
    const children = insuredChildren
      ? insuredChildren.map((adult) => ({
          ...adult,
          sanction_id: uuid(),
        }))
      : [];
    const allInsured = children && children.length ? adults.concat(children) : adults;
    const valuesForSanction = allInsured.map((insured) => {
      const { address } = adults[0];
      return {
        sanction_id: insured.sanction_id,
        title: insured.title || '',
        first_name: insured.first_name || '',
        last_name: insured.last_name || '',
        dob: dateToApi(insured.dob) || '',
        address_country: address.address_country || '',
        address_line_1: address.line1 || '',
        address_line_2: address.line2 || '',
        address_town: address.town || '',
        address_county: address.county || '',
        address_postcode: address.postcode || '',
      };
    });
    const valuesToApi = {
      meta: {
        scheme_id: schemeId,
        insured: valuesForSanction,
      },
    };

    return Promise.resolve(productActions.checkMultipleSanctions(valuesToApi)).then(
      (response) => {
        const sanctionsChecked = response.meta.sanction;
        const { passed } = response.meta;
        const newAdults = adults.map((adult) => {
          const matchingSanction = sanctionsChecked.find(
            (sanction) => sanction.sanction_id === adult.sanction_id,
          );
          return {
            ...adult,
            sanction_result: matchingSanction.sanction,
          };
        });
        const newChildren = children
          ? children.map((adult) => {
              const matchingSanction = sanctionsChecked.find(
                (sanction) => sanction.sanction_id === adult.sanction_id,
              );
              return {
                ...adult,
                sanction_result: matchingSanction.sanction,
              };
            })
          : [];
        set(values, 'data.attributes.metadata.adults', newAdults);
        set(values, 'data.attributes.metadata.children', newChildren);
        set(values, 'data.attributes.metadata.sanctions_result', passed);
        return {
          passed,
          values,
        };
      },
    );
  };

  const getQuotes = (values) => {
    return Promise.resolve(productActions.getSingleInsurerQuotes(source.id, values))
      .then((quotes) => {
        const premiums = Object.values(quotes);
        const sortedQuotes = premiums.reduce(
          (carry, premium) => {
            const schemeType = premium.meta.scheme.scheme_type;
            carry[schemeType].push(premium.meta);
            carry[schemeType].sort(sortByCoverLevel);

            return carry;
          },
          {
            standard: [],
            enhanced: [],
          },
        );
        setSortedPremiums(sortedQuotes);
      })
      .catch(() => {
        setSortedPremiums({
          standard: [],
          enhanced: [],
        });
      });
  };

  const getMtaQuotes = (values) => {
    return Promise.resolve(productActions.getSingleInsurerMta(policyId, values))
      .then((quotes) => {
        const premiums = Object.values(quotes);
        const sortedQuotes = premiums.reduce(
          (carry, premium) => {
            const schemeType = premium.meta.scheme.scheme_type;
            carry[schemeType].push(premium.meta);
            carry[schemeType].sort(sortByCoverLevel);

            return carry;
          },
          {
            standard: [],
            enhanced: [],
          },
        );
        setSortedPremiums(sortedQuotes);
      })
      .catch(() => {
        setSortedPremiums({
          standard: [],
          enhanced: [],
        });
      });
  };

  const save = (values, calculate) => {
    return productActions.saveApplication(values, null, null, isMta, calculate);
  };

  const saveAndExit = (values) => {
    const hasSchemes = !!values.data.relationships.schemes.data.length;
    set(values, 'data.attributes.metadata.generate_documents', true);
    if (values.data.id) {
      return productActions.updateApplicationAndRedirect(
        values,
        null,
        values.data.id,
        hasSchemes,
      );
    }
    return productActions.saveApplicationAndRedirect(values, null, null, hasSchemes);
  };
  const saveEmailAndExit = (values) => {
    const hasSchemes = !!values.data.relationships.schemes.data.length;
    if (values.data.id) {
      return productActions.updateApplicationEmailAndRedirect(
        values,
        null,
        values.data.id,
        hasSchemes,
      );
    }
    return productActions.saveApplicationEmailAndRedirect(values, null, null, hasSchemes);
  };

  const issue = (values) => {
    if (values.data.id) {
      return Promise.resolve(
        productActions.updateApplication(values, null, values.data.id, null, isMta, true),
      ).then((resp) => {
        set(
          values,
          'data.relationships.premium.data.id',
          get(resp, 'data.relationships.premiums.data.0.id'),
        );
        return productActions.issuePolicy(values.data.id, values);
      });
    }
    return Promise.resolve(
      productActions.saveApplication(values, null, null, isMta, false),
    ).then(() => {
      return productActions.issuePolicy(values.data.id, values);
    });
  };

  const checkSanctionsThenIssue = (values) => {
    setIssuingError('');
    return Promise.resolve(checkSanctions(values))
      .then((results) => {
        if (!results.passed) {
          if (values.data.id) {
            return Promise.resolve(
              productActions.updateApplication(
                values,
                null,
                values.data.id,
                null,
                isMta,
                true,
              ),
            ).then(() => {
              return Promise.reject(new Error('Sanction Check Failed'));
            });
          } else {
            return Promise.resolve(
              productActions.saveApplication(values, null, null, isMta, true),
            ).then(() => {
              return Promise.reject(new Error('Sanction Check Failed'));
            });
          }
        }
        return issue(results.values);
      })
      .catch((error) => {
        if (error.message === 'Sanction Check Failed') {
          setIssuingError(error.message);
        }
      });
  };

  if (!source && !isMta) {
    return null;
  }

  const directDebitQuestions =
    currentProduct.data.attributes.metadata.dd_payment_declarations;
  const initialValues =
    getApplicationInitialValues(
      application,
      user,
      isCloned,
      isMta,
      productId,
      directDebitQuestions,
    ) ||
    getNewInitialValues(
      source.id,
      customer,
      productId,
      user,
      directDebitQuestions,
      brokerId,
      channelId,
      isBrokerChannel,
    );
  const channelType = isBroker() ? 'B2B' : 'DIR';

  const rules = currentProduct.data.attributes.metadata.rules;
  return (
    <PersonalAccidentForm
      resource={initialValues}
      productName={currentProduct.data.attributes.name}
      productCode={currentProduct.data.attributes.product_code}
      productId={productId}
      source={source}
      isMta={isMta}
      calculate={getQuotes}
      calculateMta={getMtaQuotes}
      saveAndExit={saveAndExit}
      saveEmailAndExit={saveEmailAndExit}
      calculatedPremiums={sortedPremiums}
      premiums={product.quotes}
      isCalculating={product.isCalculating}
      currentProduct={currentProduct}
      isSaving={product.isSaving}
      isPremiumAdjusting={product.isPremiumAdjusting}
      purchaseDeclaration={
        currentProduct.data.attributes.metadata.purchase_declaration[channelType]
      }
      directDebitQuestions={
        currentProduct.data.attributes.metadata.dd_payment_declarations
      }
      save={save}
      issue={checkSanctionsThenIssue}
      issuingError={issuingError}
      rules={rules}
    />
  );
};

export default connect(
  (state) => ({
    product: state.product,

    auth: state.auth,
  }),
  (dispatch) => ({
    actions: bindActionCreators(actions, dispatch),
    postcodeActions: bindActionCreators(postcodeActions, dispatch),
    productActions: bindActionCreators(reduxProductActions, dispatch),
    customerActions: bindActionCreators(reduxCustomerActions, dispatch),
    dispatch,
  }),
)(PersonalAccidentFormContainer);
